/**
 * RC7 · M1-A · Connectivity foundation.
 *
 * Web: uses `navigator.onLine` + `window.online / offline` events.
 * Native: uses `@capacitor/network` — richer connectionType info.
 *
 * Contract:
 *   `useConnectivity()` hook exposes:
 *     { online: boolean, connectionType: string, since: number }
 *
 *   `subscribeConnectivity(cb)` gives non-React consumers a channel
 *   to receive status changes. Returns an unsubscribe function.
 *
 * Do NOT consume this yet for the Offline Voyage Pack — that belongs
 * to a later M1 phase. This slice establishes the foundation only.
 */
import { useEffect, useState } from "react";
import { Network } from "@capacitor/network";
import { isNativePlatform } from "@/lib/platform";

const isNative = () => isNativePlatform();

const _listeners = new Set();
let _handleRef = null;

function _emit(status) {
  _listeners.forEach((cb) => {
    try { cb(status); } catch { /* isolate one bad listener */ }
  });
}

async function _bootstrap() {
  if (isNative()) {
    if (_handleRef) return;
    _handleRef = await Network.addListener("networkStatusChange", (s) => {
      _emit({
        online: Boolean(s.connected),
        connectionType: s.connectionType || "unknown",
        since: Date.now(),
      });
    });
    // Emit an initial status so subscribers can render immediately.
    const initial = await Network.getStatus();
    _emit({
      online: Boolean(initial.connected),
      connectionType: initial.connectionType || "unknown",
      since: Date.now(),
    });
    return;
  }
  // Web branch.
  const onOnline  = () => _emit({ online: true,  connectionType: "wifi",    since: Date.now() });
  const onOffline = () => _emit({ online: false, connectionType: "none",    since: Date.now() });
  window.addEventListener("online",  onOnline);
  window.addEventListener("offline", onOffline);
  _handleRef = { remove: () => {
    window.removeEventListener("online",  onOnline);
    window.removeEventListener("offline", onOffline);
  }};
  _emit({
    online: typeof navigator !== "undefined" ? navigator.onLine !== false : true,
    connectionType: navigator?.onLine === false ? "none" : "wifi",
    since: Date.now(),
  });
}

export function subscribeConnectivity(cb) {
  _listeners.add(cb);
  _bootstrap().catch(() => { /* connectivity not fatal */ });
  return () => _listeners.delete(cb);
}

export async function getConnectivityStatus() {
  if (isNative()) {
    const s = await Network.getStatus();
    return {
      online: Boolean(s.connected),
      connectionType: s.connectionType || "unknown",
      since: Date.now(),
    };
  }
  const on = typeof navigator !== "undefined" ? navigator.onLine !== false : true;
  return { online: on, connectionType: on ? "wifi" : "none", since: Date.now() };
}

export function useConnectivity() {
  const [status, setStatus] = useState({
    online: typeof navigator !== "undefined" ? navigator.onLine !== false : true,
    connectionType: "unknown",
    since: Date.now(),
  });
  useEffect(() => {
    const unsub = subscribeConnectivity(setStatus);
    return unsub;
  }, []);
  return status;
}

export default useConnectivity;
